import { format } from 'pretty-format'

import { UserFriendlyAggregateError, UserFriendlyError } from '../errors'

import { makeUserFriendly } from './make-user-friendly'

/**
 * Returns a stringified representation of an error for _debugging_ purposes.
 * For {@link UserFriendlyError}, this means including some of the
 * non-user-friendly information
 */
export function print(error: Error): string {
  const userFriendlyError = makeUserFriendly(error)
  return format(
    {
      // If this is an aggregate error, displays each error. Otherwise, displays
      // the single error
      ...(UserFriendlyAggregateError.isUserFriendlyAggregateError(
        userFriendlyError,
      )
        ? { errors: userFriendlyError.errors.map(err => _unpackError(err)) }
        : { error: _unpackError(userFriendlyError.actualError) }),
      // Main items that are presented to the user
      forUser: {
        message: userFriendlyError.message,
        type: userFriendlyError.name,
      },
    },
    {
      maxDepth: Infinity,
    },
  )
}

/**
 * Removes the `toString` method that prevents logging all the information we
 * want to see
 */
function _unpackError(error?: Error): unknown {
  if (!error) {
    return
  }
  // NOTE: spreading hide desired information. This ensures we get everything we
  // want from the error
  return Object.assign(
    Object.create(
      UserFriendlyError.isUserFriendlyError(error)
        ? (error.actualError ?? error)
        : error,
    ),
    {
      toString: undefined,
    },
  )
}
